import { useEffect, useRef, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { useChat } from '@livekit/components-react'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { SendMessageInput } from 'modules/meetings/components/SendMessageInput'
import { useMeetingDetails } from 'modules/meetings/lib/hooks/useMeetingDetails'
import { useRoomContext } from 'modules/meetings/lib/hooks/useRoomContext'
import * as styles from 'modules/meetings/lib/utils/hiddenScrollBar.vanilla.css'

export const Chat = () => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { chatMessages, send, isSending } = useChat()
  const previousMessageCountRef = useRef(0)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const { isChatOpen, addPendingMessage } = useRoomContext()
  const { meeting } = useMeetingDetails()
  const [isHovering, setIsHovering] = useState(false)

  const attendees = meeting?.attendees || []

  useEffect(() => {
    if (chatMessages.length > previousMessageCountRef.current) {
      if (!isChatOpen) {
        addPendingMessage()
      }
    }
    previousMessageCountRef.current = chatMessages.length
  }, [chatMessages, isChatOpen, addPendingMessage])

  useEffect(() => {
    if (!isHovering && messagesContainerRef.current && chatMessages.length > 0) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [chatMessages, isHovering])

  const formattedMessages = chatMessages.map((msg) => {
    const attendee = attendees.find(
      ({ employee }) => Number(employee?.id) === Number(msg?.from?.identity)
    )
    const fullName = attendee?.employee?.fullName || i18n.t('meetings.unknown_participant')
    const avatar = buildUrl(attendee?.employee?.avatar?.url) || undefined

    return {
      from: {
        identity: msg?.from?.identity || '',
      },
      message: msg.message,
      timestamp: msg.timestamp.toString(),
      fullName,
      avatar,
    }
  })

  return (
    <>
      <Box padding="s20">
        <Text.Header2 weight="semibold" color="grey1000">
          {i18n.t('meetings.room.chat.title')}
        </Text.Header2>
      </Box>

      <Box
        ref={messagesContainerRef}
        overflowY="auto"
        flexGrow
        className={styles.hiddenScrollBar}
        paddingX="s20"
        paddingBottom="s20"
        style={{
          scrollBehavior: 'smooth',
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {formattedMessages.map((msg, index) => {
          const msgBefore = formattedMessages[index - 1] || null

          const shouldShowHeader =
            !msgBefore ||
            msg.from?.identity !== msgBefore.from?.identity ||
            (msg.from?.identity === msgBefore.from?.identity &&
              moment(msg.timestamp).diff(moment(msgBefore.timestamp), 'minutes') >= 2)

          return (
            <Box key={msg.timestamp} flexDirection="column" paddingBottom="s4">
              {shouldShowHeader && (
                <Box flexDirection="row" gap="s6" alignItems="center" paddingX="s4" paddingY="s8">
                  <Text.Normal size="200" weight="medium" color="grey1000">
                    {msg.fullName}
                  </Text.Normal>
                  <Text.Normal size="200" weight="normal">
                    •
                  </Text.Normal>
                  <Text.Normal size="100" weight="normal">
                    {moment(msg.timestamp, 'x').locale(locale).format('LT')}
                  </Text.Normal>
                </Box>
              )}
              <Box
                paddingX="s12"
                paddingY="s8"
                borderRadius={{ all: 'abs008' }}
                background="grey100"
                overflowX="hidden"
                overflowY="hidden"
              >
                <Text.Normal
                  size="200"
                  weight="normal"
                  title={moment(msg.timestamp, 'x').locale(locale).format('HH:mm:ss')}
                >
                  {msg.message}
                </Text.Normal>
              </Box>
            </Box>
          )
        })}
      </Box>

      <Box paddingX="s12" paddingBottom="s12">
        <SendMessageInput
          onSend={(msg) => send(msg)}
          isSending={isSending}
          placeholder={i18n.t('meetings.meeting.chat.write_message')}
        />
      </Box>
    </>
  )
}
